import { daysSince } from '../../utils/activityAge'
import countSectorContextAnswers from './countSectorContextAnswers'

// Zet één meetwaarde (zie projectMetricDefinitions.js) om in wat er op het
// scherm hoort te staan. Puur, zodat het overzicht en de detailpagina dezelfde
// uitkomst tonen zonder dat er twee keer geteld wordt.
//
// Vijf toestanden, en 'nul' is er maar één van. Een mislukte aanroep en een
// nog lopende aanroep beweren allebei niets over de organisatie; een 0 doet
// dat wel. Zonder dat onderscheid leest een organisatie waarvan de tellingen
// niet opgehaald konden worden als een organisatie die niets doet.
//
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading

/**
 * @typedef {object} MetricValue
 * @property {'error' | 'loading' | 'none' | 'count' | 'ratio'} state
 * @property {number} [total] - het aantal (state 'count')
 * @property {number | null} [days] - dagen sinds de laatste registratie (state 'count')
 * @property {string} [text] - 'teller/noemer' (state 'ratio')
 */

/**
 * @param {import('./projectMetricDefinitions').ProjectMetric} metric
 * @param {object} source
 * @param {Record<string, object> | null} source.byCompagnyId - tellingen per organisatie; null = nog geen antwoord
 * @param {string} source.compagnyId - de organisatie waar het om gaat
 * @param {boolean} source.failed - de aanroep is mislukt
 * @param {{SectorContext?: Record<string, Record<string, string>>}} [source.project] - het CompagnyMeta-document
 * @returns {MetricValue}
 */
const projectMetricValue = (metric, { byCompagnyId, compagnyId, failed, project }) => {

    // Contextinfo staat op het project zelf en niet in de tellingen, dus die
    // hangt niet aan het laden of falen van de serveraanroep.
    if (metric.type === 'context') {
        const counts = countSectorContextAnswers(project?.SectorContext, metric.sector)
        if (!counts) return { state: 'none' }
        return { state: 'ratio', text: `${counts.answered}/${counts.total}` }
    }

    if (failed) return { state: 'error' }
    if (!byCompagnyId) return { state: 'loading' }

    const counts = byCompagnyId[compagnyId] || {}

    if (metric.type === 'ratio') {
        const denominator = counts[metric.denominatorKey] ?? 0
        // Een verhouding zonder noemer is geen 0 van 0 maar een niet-gestelde
        // vraag: zonder effecten valt er niets compleet te zijn.
        if (!denominator) return { state: 'none' }
        return { state: 'ratio', text: `${counts[metric.numeratorKey] ?? 0}/${denominator}` }
    }

    const lastMs = metric.lastMsKey ? counts[metric.lastMsKey] ?? null : null

    return {
        state: 'count',
        total: counts[metric.countKey] ?? 0,
        days: lastMs !== null ? daysSince({ toDate: () => new Date(lastMs) }) : null,
    }
}

export default projectMetricValue
